import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../../config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../Layout/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import {
  User,
  Mail,
  Phone,
  MessageSquare,
  Upload,
  Send,
  AlertTriangle,
  CheckCircle,
  Clock,
  Loader2,
} from "lucide-react";
import Loader from "../utils/Loader";

const ProfileManagement = () => {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState("profile");
  const [profileData, setProfileData] = useState(null);
  const [username, setUsername] = useState({
    name: "No data",
    username: "No data",
    email: "No data",
    phone: "No data",
  });
  const [loading, setLoading] = useState(true);
  const [showExpiryNotification, setShowExpiryNotification] = useState(false);
  const [images, setImages] = useState([]);
  const [queryLoading, setQueryLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm();

  const tabs = [
    { id: "profile", label: "Profile", icon: User },
    { id: "contact", label: "Contact", icon: Mail },
    { id: "query", label: "Raise Query", icon: MessageSquare },
  ];

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;

    return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  };

  const isExpiringSoon = (expiryDate) => {
    const now = new Date();
    const expiry = new Date(expiryDate);
    const twoDaysFromNow = new Date(now.setDate(now.getDate() + 2));
    return expiry <= twoDaysFromNow && expiry > new Date();
  };

  const validityValue = profileData?.activeUpto
    ? formatDateTime(profileData.activeUpto)
    : "No data";

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        const token = window.__appSession.getToken();
        const apiKey = window.__appSession.getItem("apiKey");

        if (!apiKey) {
          setProfileData({
            accountType: "No data",
            activeUpto: null,
            roles: [],
            channels: [],
          });
          setLoading(false);
          return;
        }

        const profileResponse = await axios.get(
          `${API_BASE_URL}/api/account/detail`,
          {
            headers: {
              "x-api-key": apiKey,
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (profileResponse.data.status === 200) {
          setProfileData(profileResponse.data.result);
          if (isExpiringSoon(profileResponse.data.result.activeUpto)) {
            setShowExpiryNotification(true);
          }
        } else {
          toast.error("Failed to fetch profile data.");
        }
      } catch (error) {
        console.error("Error fetching profile data:", error);
        toast.error("Enter Valid API Key");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  useEffect(() => {
    const fetchUsername = async () => {
      const token = window.__appSession.getToken();
      try {
        const messagesResponse = await axios.get(
          `${API_BASE_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (messagesResponse.data.result) {
          const userData = messagesResponse.data.result;
          setUsername(userData);
          setValue("name", userData.name || "");
          setValue("phone", userData.phone || "");
        } else {
          setUsername({
            name: "No data",
            username: "No data",
            email: "No data",
            phone: "No data",
          });
        }
      } catch (error) {
        console.error("Error fetching username:", error);
        setUsername({
          name: "No data",
          username: "No data",
          email: "No data",
          phone: "No data",
        });
      }
    };

    fetchUsername();
  }, [setValue]);

  const onSubmit = async (data) => {
    const token = window.__appSession.getToken();
    if (!token) {
      toast.error("No authentication token found. Please log in.");
      return;
    }

    const formData = new FormData();
    images.forEach((image) => formData.append("images", image));
    formData.append("name", data.name);
    formData.append("phone", data.phone);
    formData.append("problem", data.problem);

    setQueryLoading(true);

    try {
      await axios.post(`${API_BASE_URL}/api/raise-query`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("Your query has been raised successfully!");
      setImages([]);
      reset({ name: username.name, phone: username.phone, problem: "" });
    } catch (error) {
      console.error("Error submitting query:", error);
      toast.error("There was an error submitting your query.");
    } finally {
      setQueryLoading(false);
    }
  };

  const handleImageChange = (e) => {
    setImages([...e.target.files]);
  };

  const ProfileDetail = ({ label, value, className, icon: Icon }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`glass-surface flex items-center justify-between rounded-xl border p-3 text-xs transition-all ${theme.contentBg} ${theme.contentBorder} ${theme.sidebarHover} ${className}`}
    >
      <div className="flex items-center gap-2">
        {Icon && <Icon size={14} className={theme.iconColor} />}
        <span className={`${theme.sidebarText} font-medium`}>{label}:</span>
      </div>
      <span className={`${theme.sidebarText} opacity-80 text-right`}>
        {value}
      </span>
    </motion.div>
  );

  const renderProfileContent = () => (
    <div className="space-y-3">
      <ProfileDetail label="Name" value={username.name} icon={User} />
      <ProfileDetail label="Username" value={username.username} icon={User} />
      <ProfileDetail label="Email" value={username.email} icon={Mail} />
      <ProfileDetail
        label="Mobile Number"
        value={username.phone}
        icon={Phone}
      />
      <ProfileDetail
        label="Account Type"
        value={profileData?.accountType || "No data"}
        icon={CheckCircle}
        className={`${
          profileData?.accountType === "No data" ||
          profileData?.accountType === "Demo"
            ? "bg-violet-50 border-violet-200"
            : "bg-emerald-50 border-emerald-200"
        }`}
      />
      <ProfileDetail label="Validity" value={validityValue} icon={Clock} />

      {showExpiryNotification && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800"
        >
          <div className="flex items-center gap-2 font-medium text-xs">
            <AlertTriangle size={14} />
            Your validity is expiring soon!
          </div>
          <p className="mt-1 text-xs">
            Please{" "}
            <button
              onClick={() => setActiveTab("contact")}
              className="font-medium text-indigo-600 underline transition-colors hover:text-indigo-800"
            >
              contact us
            </button>{" "}
            to buy a premium API key.
          </p>
        </motion.div>
      )}

      {!profileData?.activeUpto ||
      new Date(profileData.activeUpto) < new Date() ? (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-4 rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-800"
        >
          <div className="flex items-center gap-2 font-medium text-xs">
            <AlertTriangle size={14} />
            Your validity has expired!
          </div>
          <p className="mt-1 text-xs">
            Please{" "}
            <button
              onClick={() => setActiveTab("contact")}
              className="font-medium text-indigo-600 underline transition-colors hover:text-indigo-800"
            >
              contact us
            </button>{" "}
            to buy a premium API key.
          </p>
        </motion.div>
      ) : null}
    </div>
  );

  const renderContactContent = () => (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className={`glass-surface border ${theme.sidebarBorder} rounded-2xl p-4 text-center transition-all hover:shadow-md`}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Mail size={16} className={theme.iconColor} />
          <h3 className={`text-sm font-semibold ${theme.sidebarText}`}>
            Email Us
          </h3>
        </div>
        <p className={`text-xs ${theme.sidebarText} opacity-70 mb-3`}>
          Feel free to send us an email:
        </p>
        <a
          href="mailto:Info@smartitbox.in"
          className={`${theme.iconColor} text-xs font-medium hover:underline transition-colors`}
        >
          Info@smartitbox.in
        </a>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`glass-surface border ${theme.sidebarBorder} rounded-2xl p-4 text-center transition-all hover:shadow-md`}
      >
        <div className="flex items-center justify-center gap-2 mb-2">
          <Phone size={16} className={theme.iconColor} />
          <h3 className={`text-sm font-semibold ${theme.sidebarText}`}>
            Call Us
          </h3>
        </div>
        <p className={`text-xs ${theme.sidebarText} opacity-70 mb-3`}>
          You can reach us by phone:
        </p>
        <a
          href="tel:+918716808360"
          className={`${theme.iconColor} text-xs font-medium hover:underline transition-colors`}
        >
          +91 87168 08360
        </a>
      </motion.div>
    </div>
  );

  const renderQueryContent = () => (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label
          className={`block ${theme.sidebarText} font-medium mb-1 text-xs`}
        >
          Name
        </label>
        <div className="relative">
          <User
            size={14}
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.iconColor}`}
          />
          <input
            type="text"
            value={username.name}
            {...register("name")}
            readOnly
            className={`w-full cursor-not-allowed rounded-xl border border-indigo-200 bg-white/85 pl-9 pr-3 py-2 text-xs text-slate-600 opacity-80 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
          />
        </div>
      </div>

      <div>
        <label
          className={`block ${theme.sidebarText} font-medium mb-1 text-xs`}
        >
          Phone
        </label>
        <div className="relative">
          <Phone
            size={14}
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.iconColor}`}
          />
          <input
            type="text"
            {...register("phone", { required: "Phone number is required" })}
            className={`w-full rounded-xl border border-indigo-200 bg-white/90 pl-9 pr-3 py-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
          />
        </div>
        {errors.phone && (
          <span className="text-red-500 text-xs mt-1 block">
            {errors.phone.message}
          </span>
        )}
      </div>

      <div>
        <label
          className={`block ${theme.sidebarText} font-medium mb-1 text-xs`}
        >
          Problem Description
        </label>
        <div className="relative">
          <MessageSquare
            size={14}
            className={`absolute left-3 top-3 ${theme.iconColor}`}
          />
          <textarea
            {...register("problem", {
              required: "Problem description is required",
            })}
            className={`w-full resize-none rounded-xl border border-indigo-200 bg-white/90 pl-9 pr-3 py-2 text-xs text-slate-700 transition-all focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
            rows="3"
          />
        </div>
        {errors.problem && (
          <span className="text-red-500 text-xs mt-1 block">
            {errors.problem.message}
          </span>
        )}
      </div>

      <div>
        <label
          className={`block ${theme.sidebarText} font-medium mb-1 text-xs`}
        >
          Attach Screenshots
        </label>
        <div className="relative">
          <Upload
            size={14}
            className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${theme.iconColor}`}
          />
          <input
            type="file"
            multiple
            onChange={handleImageChange}
            className={`w-full rounded-xl border border-indigo-200 bg-white/90 pl-9 pr-3 py-2 text-xs text-slate-700 transition-all file:mr-2 file:rounded file:border-0 file:bg-indigo-100 file:px-2 file:py-1 file:text-xs file:text-indigo-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
          />
        </div>
        {images.length > 0 && (
          <div className={`mt-1 text-xs ${theme.sidebarText} opacity-70`}>
            {images.length} file(s) selected
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={queryLoading}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2 text-xs font-medium text-white transition-all hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {queryLoading ? (
          <>
            <Loader2 size={14} className="animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send size={14} />
            Submit Query
          </>
        )}
      </button>
    </form>
  );

  return (
    <Layout>
      <div className="transition-colors duration-300">
        {/* Loading Overlay */}
        {loading && <Loader theme={theme} text="Loading profile..." />}

        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className={`glass-surface-strong ${theme.contentBg} rounded-2xl border ${theme.contentBorder} p-5 mb-4`}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2">
                <User size={20} className="text-white" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">
                Profile Management
              </h1>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-medium transition-all ${
                      activeTab === tab.id
                        ? "border-indigo-500/40 bg-gradient-to-r from-indigo-600 to-violet-600 text-white"
                        : `${theme.sidebarText} ${theme.sidebarHover} border-indigo-200 bg-white/80`
                    }`}
                  >
                    <Icon size={14} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={`glass-surface ${theme.contentBg} rounded-2xl border ${theme.contentBorder} p-5`}
          >
            {activeTab === "profile" && renderProfileContent()}
            {activeTab === "contact" && renderContactContent()}
            {activeTab === "query" && renderQueryContent()}
          </motion.div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    </Layout>
  );
};

export default ProfileManagement;

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { motion } from "framer-motion"; // For animations
// import { API_BASE_URL } from "../../config";
// import Layout from "../Layout/Layout"; // Import the enhanced Layout component
// import "./style.css";

// const Footer = () => {
//   return (
//     <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
//       <p className="text-sm tracking-wide">
//         Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
//       </p>
//     </footer>
//   );
// };

// const ProfilePage = () => {
//   const [profileData, setProfileData] = useState(null);
//   const [username, setUsername] = useState({
//     name: "No data",
//     username: "No data",
//     email: "No data",
//     phone: "No data",
//   });
//   const [loading, setLoading] = useState(true);
//   const [showExpiryNotification, setShowExpiryNotification] = useState(false);

//   const formatDateTime = (dateString) => {
//     const date = new Date(dateString);
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = String(date.getFullYear()).slice(-2);
//     const hours = String(date.getHours()).padStart(2, "0");
//     const minutes = String(date.getMinutes()).padStart(2, "0");
//     const seconds = String(date.getSeconds()).padStart(2, "0");
//     const ampm = hours >= 12 ? "PM" : "AM";
//     const formattedHours = hours % 12 || 12;

//     return `${day}/${month}/${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
//   };

//   const isExpiringSoon = (expiryDate) => {
//     const now = new Date();
//     const expiry = new Date(expiryDate);
//     const twoDaysFromNow = new Date(now.setDate(now.getDate() + 2));
//     return expiry <= twoDaysFromNow && expiry > new Date();
//   };

//   const validityValue = profileData?.activeUpto
//     ? formatDateTime(profileData.activeUpto)
//     : "No data";

//   useEffect(() => {
//     const fetchProfileData = async () => {
//       setLoading(true);
//       try {
//         const token = window.__appSession.getToken();
//         const apiKey = window.__appSession.getItem("apiKey");

//         if (!apiKey) {
//           setProfileData({
//             accountType: "No data",
//             activeUpto: null,
//             roles: [],
//             channels: [],
//           });
//           setLoading(false);
//           return;
//         }

//         const profileResponse = await axios.get(
//           `${API_BASE_URL}/api/account/detail`,
//           {
//             headers: {
//               "x-api-key": apiKey,
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );

//         if (profileResponse.data.status === 200) {
//           setProfileData(profileResponse.data.result);
//           if (isExpiringSoon(profileResponse.data.result.activeUpto)) {
//             setShowExpiryNotification(true);
//           }
//         } else {
//           toast.error("Failed to fetch profile data.");
//         }
//       } catch (error) {
//         console.error("Error fetching profile data:", error);
//         toast.error("Enter Valid API Key");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchProfileData();
//   }, []);

//   useEffect(() => {
//     const fetchUsername = async () => {
//       const token = window.__appSession.getToken();
//       try {
//         const messagesResponse = await axios.get(
//           `${API_BASE_URL}/api/auth/profile`,
//           {
//             headers: {
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );

//         if (messagesResponse.data.result) {
//           setUsername(messagesResponse.data.result);
//         } else {
//           setUsername({
//             name: "No data",
//             username: "No data",
//             email: "No data",
//             phone: "No data",
//           });
//         }
//       } catch (error) {
//         console.error("Error fetching username:", error);
//         setUsername({
//           name: "No data",
//           username: "No data",
//           email: "No data",
//           phone: "No data",
//         });
//       }
//     };

//     fetchUsername();
//   }, []);

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
//       >
//         {loading && (
//           <motion.div
//             className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           >
//             <svg
//               className="animate-spin h-12 w-12 text-[#25D366]"
//               xmlns="http://www.w3.org/2000/svg"
//               viewBox="0 0 24 24"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               />
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
//               />
//             </svg>
//           </motion.div>
//         )}

//         <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-xl mx-auto border border-gray-200 transition-all">
//           <h1 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-[#25D366] tracking-tight font-serif">
//             Profile Details
//           </h1>

//           <div className="space-y-4">
//             <ProfileDetail label="Name" value={username.name} />
//             <ProfileDetail label="Username" value={username.username} />
//             <ProfileDetail label="Email" value={username.email} />
//             <ProfileDetail label="Mobile Number" value={username.phone} />
//             <ProfileDetail
//               label="Account Type"
//               value={profileData?.accountType || "No data"}
//               className={`${
//                 profileData?.accountType === "No data" ||
//                 profileData?.accountType === "Demo"
//                   ? "bg-yellow-100 text-yellow-800"
//                   : "bg-green-100 text-green-800"
//               }`}
//             />
//             <ProfileDetail label="Validity" value={validityValue} />
//           </div>

//           {showExpiryNotification && (
//             <motion.div
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5 }}
//               className="mt-6 p-4 bg-yellow-100 text-yellow-900 border border-yellow-300 rounded-lg shadow-md"
//             >
//               <strong className="flex items-center">
//                 âš ï¸ Your validity is expiring soon!
//               </strong>
//               <p className="mt-1">
//                 Please{" "}
//                 <a
//                   href="/contactus"
//                   className="text-primary-600 underline font-semibold hover:text-primary-800 transition-all"
//                 >
//                   buy a premium API key
//                 </a>{" "}
//                 to continue enjoying our services.
//               </p>
//             </motion.div>
//           )}

//           {!profileData?.activeUpto ||
//           new Date(profileData.activeUpto) < new Date() ? (
//             <motion.div
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5 }}
//               className="mt-6 p-4 bg-red-100 text-red-900 border border-red-300 rounded-lg shadow-md"
//             >
//               <strong className="flex items-center">
//                 âš ï¸ Your validity has expired!
//               </strong>
//               <p className="mt-1">
//                 Please{" "}
//                 <a
//                   href="/contactus"
//                   className="text-primary-600 underline font-semibold hover:text-primary-800 transition-all"
//                 >
//                   buy a premium API key
//                 </a>{" "}
//                 to continue enjoying our services.
//               </p>
//             </motion.div>
//           ) : null}
//         </div>
//       </motion.main>
//       <ToastContainer />
//     </Layout>
//   );
// };

// const ProfileDetail = ({ label, value, className }) => (
//   <motion.div
//     initial={{ opacity: 0, y: 10 }}
//     animate={{ opacity: 1, y: 0 }}
//     transition={{ duration: 0.3 }}
//     className={`flex justify-between text-sm sm:text-base font-medium rounded-lg p-3 shadow-sm transition-all bg-gray-50/80 backdrop-blur-sm border border-gray-200 hover:scale-[1.01] hover:shadow-md ${className}`}
//   >
//     <span className="text-gray-700">{label}:</span>
//     <span className="font-normal text-gray-800">{value}</span>
//   </motion.div>
// );

// export default ProfilePage;
